import { flexRender, Row } from "@tanstack/react-table"
import { TableCell, TableRow } from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { AnimatePresence } from "framer-motion"
import { type Features } from "lib/table"
import { isNil } from "lopermut"
import { memo } from "react"
import { Fragment } from "react/jsx-runtime"
import { CampaignLogResponseDataSchema } from "schema"
import z from "zod"
import { ExpandedRow } from "./ExpandedRow"

type MemoizedRowProps = {
  row: Row<Features, z.infer<typeof CampaignLogResponseDataSchema>>
  expandedRows: Set<string>
  onToggleExpand: (id: string) => void
}

// NOTE: Using a memoized row to avoid re-rendering the row when the expanded rows change
const MemoizedRow = memo(({ row, expandedRows, onToggleExpand }: MemoizedRowProps) => {
  const isExpanded = expandedRows.has(row.original.id)
  const isExpandable = !isNil(row.original.log)
  const colSpan = row.getVisibleCells().length

  return (
    <Fragment>
      <TableRow
        className={cn(isExpandable && "cursor-pointer hover:bg-muted/50 transition-colors")}
        onClick={isExpandable ? () => onToggleExpand(row.original.id) : undefined}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id} {...cell.column.columnDef.meta} className="py-2">
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
      <AnimatePresence>
        {isExpanded && (
          <tr>
            <td colSpan={colSpan}>
              <ExpandedRow data={row.original} />
            </td>
          </tr>
        )}
      </AnimatePresence>
    </Fragment>
  )
})
MemoizedRow.displayName = "MemoizedRow"

export { MemoizedRow as Row }
